import { useRef, type DragEvent } from 'react';
import { ArrowClockwise, FilePdf, FileVideo, FileZip, Image as ImageIcon, Plus, X } from '@phosphor-icons/react';
import { BRIEF_UPLOAD_TYPES } from '../../lib/asset-upload';
import type { UploadItem, useAssetUploads } from '../../lib/use-asset-uploads';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';
import { ProgressBar } from '../ui/progress-bar';
import { UploadRow } from '../ui/upload-row';
import { AssetImage } from './asset-image';

const iconFor = (contentType: string) =>
  contentType.startsWith('video/')
    ? FileVideo
    : contentType === 'application/pdf'
      ? FilePdf
      : contentType === 'application/zip'
        ? FileZip
        : ImageIcon;

function Tile({ item, onRemove, onRetry }: { item: UploadItem; onRemove: () => void; onRetry: () => void }) {
  const failed = item.status === 'failed';
  return (
    <li
      className={cx(
        'relative flex aspect-square flex-col justify-end overflow-hidden rounded-(--mbm-radius-md) bg-surface-tint p-(--mbm-space-2)',
        failed && 'outline outline-1 outline-feedback-error',
      )}
    >
      {item.previewUrl ? (
        <img src={item.previewUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : item.asset && item.contentType.startsWith('image/') ? (
        <AssetImage asset={item.asset} className="absolute inset-0" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center text-text-secondary">
          <Icon icon={iconFor(item.contentType)} size="xl" />
        </span>
      )}

      <button
        type="button"
        aria-label={`Remove ${item.name}`}
        onClick={onRemove}
        className="absolute right-(--mbm-space-2) top-(--mbm-space-2) flex size-(--mbm-space-6) items-center justify-center rounded-full bg-surface-card text-text-primary shadow-(--mbm-shadow-sm) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <Icon icon={X} size="xs" />
      </button>

      <div className="relative flex flex-col gap-(--mbm-space-1)">
        {item.status === 'uploading' ? (
          <ProgressBar value={item.progress * 100} label={`Uploading ${item.name}`} />
        ) : null}
        {failed ? (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-(--mbm-space-1) self-start rounded-full bg-surface-card px-(--mbm-space-2) text-label-small text-feedback-error focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={ArrowClockwise} size="xs" />
            Retry
          </button>
        ) : null}
        <span className="truncate rounded-(--mbm-radius-xs) bg-overlay-scrim px-(--mbm-space-1-5) text-label-small text-text-on-dark">
          {item.name}
        </span>
      </div>
    </li>
  );
}

const megabytes = (bytes: number) => `${bytes >= 1024 * 1024 ? Math.round(bytes / 1024 / 1024) : '<1'} MB`;

/** One file as a line (Figma P01 "Uploads"): its name, size and progress, and the way to cancel, retry or remove it. */
function FileRow({ item, onRemove, onRetry }: { item: UploadItem; onRemove: () => void; onRetry: () => void }) {
  return (
    <UploadRow
      name={item.name}
      sizeLabel={megabytes(item.size)}
      status={item.status}
      progress={item.progress}
      message={item.status === 'done' ? undefined : item.error}
      onCancel={onRemove}
      onRemove={onRemove}
      onRetry={onRetry}
    />
  );
}

/**
 * "Brief assets" (Figma A05.3): tiles for each file with upload progress, retry and remove, plus the drop zone.
 * Files go straight to storage; the form submits only the ones that finished.
 */
export function AssetUploader({
  uploads,
  hint,
  title = 'Brief assets',
  accept = BRIEF_UPLOAD_TYPES,
  dropHint = 'Drag in images, video or PDF · up to 200 MB',
  inputLabel = 'Add brief files',
  layout = 'tiles',
}: {
  uploads: ReturnType<typeof useAssetUploads>;
  hint: string;
  title?: string;
  accept?: readonly string[];
  dropHint?: string;
  inputLabel?: string;
  /** `rows` lists each file on a line, for a build's zip or a reel's video; `tiles` is the picture grid. */
  layout?: 'tiles' | 'rows';
}) {
  const input = useRef<HTMLInputElement>(null);
  const { items, notice, addFiles, remove, retry } = uploads;
  const attached = items.filter((item) => item.status !== 'rejected').length;
  const refused = items.filter((item) => item.status === 'rejected');

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="flex flex-col gap-(--mbm-space-3)">
      <div className="flex flex-col gap-(--mbm-space-0-5)">
        <span className="text-label-medium text-text-primary">{title}</span>
        <span className="text-body-small text-text-tertiary">{hint}</span>
      </div>

      {layout === 'rows' ? (
        <ul className="flex flex-col gap-(--mbm-space-2)">
          {items.map((item) => (
            <FileRow key={item.id} item={item} onRemove={() => remove(item.id)} onRetry={() => retry(item.id)} />
          ))}
          <li>
            <button
              type="button"
              onClick={() => input.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="flex w-full items-center justify-center gap-(--mbm-space-2) rounded-(--mbm-radius-sm) border-[1.5px] border-dashed border-border-default px-(--mbm-space-3-5) py-(--mbm-space-3) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={Plus} size="sm" />
              {attached > 0 ? 'Replace or add a file' : 'Add a file'}
              <span className="text-body-small text-text-tertiary">· {dropHint}</span>
            </button>
          </li>
        </ul>
      ) : (
        <ul className="grid grid-cols-2 gap-(--mbm-space-3) sm:grid-cols-4">
          {items
            .filter((item) => item.status !== 'rejected')
            .map((item) => (
              <Tile key={item.id} item={item} onRemove={() => remove(item.id)} onRetry={() => retry(item.id)} />
            ))}
          <li className={cx(attached > 0 ? 'col-span-2 sm:col-span-3' : 'col-span-2 sm:col-span-4')}>
            <button
              type="button"
              onClick={() => input.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="flex size-full min-h-(--mbm-size-list-row-tall) flex-col items-center justify-center gap-(--mbm-space-1) rounded-(--mbm-radius-md) border-[1.5px] border-dashed border-border-default bg-surface-card p-(--mbm-space-4) text-center focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={Plus} size="md" />
              <span className="text-label-medium text-text-primary">Add files</span>
              <span className="text-body-small text-text-tertiary">{dropHint}</span>
            </button>
          </li>
        </ul>
      )}

      {layout === 'tiles' && refused.length > 0 ? (
        <ul className="flex flex-col gap-(--mbm-space-2)">
          {refused.map((item) => (
            <FileRow key={item.id} item={item} onRemove={() => remove(item.id)} onRetry={() => retry(item.id)} />
          ))}
        </ul>
      ) : null}

      <input
        ref={input}
        type="file"
        multiple
        hidden
        accept={accept.join(',')}
        aria-label={inputLabel}
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      {notice ? (
        <p role="alert" className="text-body-small text-feedback-error">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
